import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { ProspectList } from '@/types/prospect';
import { useOptimizedCampaignData } from '@/contexts/OptimizedCampaignDataContext';
import { useToast } from '@/hooks/use-toast';
import { AlertTriangle } from 'lucide-react';
interface ProspectListCreatorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingList?: ProspectList | null;
  onListCreated?: (list: ProspectList) => void;
}
export function ProspectListCreator({
  open,
  onOpenChange,
  editingList,
  onListCreated,
}: ProspectListCreatorProps) {
  const { addProspectList, updateProspectList } = useOptimizedCampaignData();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });
  const [tcpaAcknowledged, setTcpaAcknowledged] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    if (editingList) {
      setFormData({
        name: editingList.name,
        description: editingList.description || '',
      });
      setTcpaAcknowledged(editingList.tcpa_compliance || false);
    } else if (!open) {
      setFormData({
        name: '',
        description: '',
      });
      setTcpaAcknowledged(false);
    }
  }, [editingList, open]);
  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a list name.',
        variant: 'destructive',
      });
      return;
    }
    if (!editingList && !tcpaAcknowledged) {
      toast({
        title: 'TCPA Compliance Required',
        description:
          'Please acknowledge TCPA compliance requirements before creating the list.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    try {
      if (editingList) {
        // Update existing list
        const updateData = {
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          prospects: editingList.prospects,
          tcpa_compliance: tcpaAcknowledged,
          tcpa_acknowledged_at: tcpaAcknowledged
            ? new Date()
            : editingList.tcpa_acknowledged_at,
          tcpa_acknowledged_by: tcpaAcknowledged
            ? 'User'
            : editingList.tcpa_acknowledged_by,
        };
        updateProspectList(editingList.id, updateData);
        toast({
          title: 'List Updated',
          description: 'Prospect list has been updated successfully.',
        });
      } else {
        // Create new list
        const newListData = {
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          prospects: [],
          tcpa_compliance: true,
          tcpa_acknowledged_at: new Date().toISOString(),
          tcpa_acknowledged_by: 'User',
        };
        const newList = await addProspectList(newListData);
        toast({
          title: 'List Created Successfully',
          description:
            'Prospect list created successfully. You can now add prospects to it.',
        });
        if (onListCreated) {
          onListCreated(newList as any);
        }
      }
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to save prospect list:', error);
      toast({
        title: 'Error',
        description: 'Failed to save prospect list. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-md'>
        <DialogHeader>
          <DialogTitle>
            {editingList ? 'Edit Prospect List' : 'Create New Prospect List'}
          </DialogTitle>
          <DialogDescription>
            {editingList
              ? 'Update the details of your prospect list.'
              : 'Create a new prospect list for organizing your contacts. TCPA compliance is required for voice prospecting.'}
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4'>
          <div>
            <Label htmlFor='list-name'>List Name *</Label>
            <Input
              id='list-name'
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }
              placeholder='e.g., HVAC Contractors - Q1 2024'
            />
          </div>

          <div>
            <Label htmlFor='list-description'>Description</Label>
            <Textarea
              id='list-description'
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder='Optional description of this prospect list...'
              rows={3}
            />
          </div>

          {(!editingList || !editingList?.tcpa_compliance) && (
            <div className='border-2 border-orange-200 p-4 rounded-lg bg-orange-50/50'>
              <div className='flex items-start space-x-3 bg-slate-50'>
                <Checkbox
                  id='tcpa-acknowledgment'
                  checked={tcpaAcknowledged}
                  onCheckedChange={(checked) =>
                    setTcpaAcknowledged(checked === true)
                  }
                  className='mt-1'
                />
                <div className='flex-1'>
                  <label
                    htmlFor='tcpa-acknowledgment'
                    className='text-sm font-medium leading-relaxed cursor-pointer flex items-start gap-2'
                  >
                    <AlertTriangle className='h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0' />
                    <div>
                      <div className='font-semibold text-orange-800'>
                        TCPA Compliance Required
                      </div>
                      <div className='text-orange-700 mt-1'>
                        I acknowledge TCPA compliance and consent requirements
                        for voice prospecting. I certify that this list meets
                        all regulatory requirements and regulations.
                      </div>
                      <a
                        href='https://www.fcc.gov/consumers/guides/stop-unwanted-robocalls-and-texts'
                        target='_blank'
                        rel='noopener noreferrer'
                        className='inline-flex items-center text-xs text-orange-600 hover:text-orange-800 underline mt-2'
                      >
                        View TCPA Regulations →
                      </a>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          )}

          <div className='flex gap-2 pt-4'>
            <Button
              variant='outline'
              onClick={() => onOpenChange(false)}
              className='flex-1'
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className='flex-1'
              disabled={isLoading || (!editingList && !tcpaAcknowledged)}
            >
              {isLoading
                ? editingList
                  ? 'Updating...'
                  : 'Creating...'
                : editingList
                ? 'Update List'
                : 'Create List'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
